import { Badge, Col, Input, Row, Table, Tag } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import VuiPageTitle from "../../../components/page-title";
import { useTranslation } from "react-i18next";
import { SearchOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/lib/table/interface";
import { InboxList } from "./interface";
import VuiTableActionButton from "../../../components/table-action-button";
import { useGetInbox } from "../hooks/use-get-inbox";
import { IPaginationParams } from "../../../utils/interfaces/pagination-params.interface";
import {
  inboxStatusOptions,
  openNotification,
  overrideTableSortOrder,
  snakeCaseToTitleCase,
  uiFilterData,
} from "../../../utils/helpers";
import useDebounce from "../../../hooks/use-debounce";
import moment from "moment/moment";
import {
  DEFAULT_PAGE_SIZE,
  INBOX_STATUS_COLOR,
  LOCALSTORAGE_FILTER_DATA_KEY,
} from "../../../constants";
import _ from "lodash";
import { ValueType } from "../../../components/select/interface";
import VuiSelect from "../../../components/select";
import VuiModalConfirmation from "../../../components/modal-confirmation";
import { useFormInbox } from "../hooks/use-form-inbox";

const qs = require("qs-lite");

const InboxListModule = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, loadData, isOnFetchingData, totalData } = useGetInbox();
  const { onDelete, deleteLoading } = useFormInbox();
  const [hasInitialize, setHasInitialize] = useState<boolean>(false);

  const [selectedStatus, setSelectedStatus] = useState<ValueType | undefined>();

  const [search, setSearch] = useState<string>("");
  const debouncedSearch = useDebounce(search, 250);
  const [tableDeleteId, setTableDeleteId] = useState<number>(0);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [tableQuery, setTableQuery] = useState<IPaginationParams>({
    page: Number(searchParams.get("page")) || 1,
    per_page: Number(searchParams.get("perPage")) || DEFAULT_PAGE_SIZE,
    sorted_by: searchParams.get("sortedBy"),
    order_by: searchParams.get("orderBy") as string,
  });

  const handleChangeSearchParams = useCallback(async () => {
    const params = {} as IPaginationParams;

    Object.assign(params, {
      page: tableQuery.page,
    });

    Object.assign(params, {
      perPage: tableQuery.per_page,
    });

    if (debouncedSearch) {
      Object.assign(params, {
        search: debouncedSearch,
      });
    }

    if (tableQuery.order_by) {
      Object.assign(params, {
        orderBy: tableQuery.order_by,
      });
    }

    if (tableQuery.sorted_by) {
      Object.assign(params, {
        sortedBy: tableQuery.sorted_by,
      });
    }

    if (selectedStatus) {
      Object.assign(params, {
        status: selectedStatus.value,
      });
    } else {
      const recentStatus = uiFilterData.get(
        LOCALSTORAGE_FILTER_DATA_KEY.status
      );
      if (!recentStatus) {
        searchParams.delete("status");
      } else {
        const paramsStatusIds = _.map(searchParams.getAll("status"), (item) =>
          parseInt(item)
        );

        if (paramsStatusIds.length) {
          const statusFromStorage = uiFilterData.getSelectedFilter(
            recentStatus,
            paramsStatusIds
          );

          setSelectedStatus(statusFromStorage[0]);

          Object.assign(params, {
            status: statusFromStorage[0]?.value,
          });
        } else {
          uiFilterData.remove(LOCALSTORAGE_FILTER_DATA_KEY.status);
        }
      }
    }

    const queryParams = qs.stringify(params, { indices: false });

    if (queryParams) {
      setSearchParams(`?${queryParams}`);
    } else {
      navigate("/access");
    }

    const tableParams: IPaginationParams = {
      page: params.page,
      per_page: tableQuery.per_page,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(params.status ? { status: params.status } : {}),

      ...(tableQuery.order_by
        ? { order_by: tableQuery.order_by }
        : { order_by: "created_at" }),
      ...(tableQuery.sorted_by
        ? { sorted_by: overrideTableSortOrder(tableQuery.sorted_by) }
        : { sorted_by: "DESC" }),
    };

    await loadData(tableParams);

    setHasInitialize(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tableQuery,
    debouncedSearch,
    loadData,
    navigate,
    searchParams,
    setSearchParams,
    selectedStatus,
  ]);

  const columns: ColumnsType<InboxList> = [
    {
      title: null,
      dataIndex: "is_read",
      key: "is_read",
      sorter: false,
      width: 30,
      defaultSortOrder:
        tableQuery.order_by === "is_read" && tableQuery.sorted_by,
      render: (isRead) => <Badge status={isRead ? "default" : "error"} />,
    },
    {
      title: t("common.text.date"),
      dataIndex: "created_at",
      key: "created_at",
      sorter: true,
      width: 150,
      defaultSortOrder:
        tableQuery.order_by === "created_at" && tableQuery.sorted_by,
      render: (date) => moment(date).format("D MMM YYYY | HH:mm"),
    },
    {
      title: t("common.text.category"),
      dataIndex: "category",
      key: "category",
      width: 200,
      sorter: true,
      defaultSortOrder:
        tableQuery.order_by === "category" && tableQuery.sorted_by,
    },
    {
      title: t("common.text.meetingDate"),
      dataIndex: "start_time",
      key: "start_time",
      sorter: true,
      width: 150,
      defaultSortOrder:
        tableQuery.order_by === "start_time" && tableQuery.sorted_by,
      render: (date) =>
        date ? moment(date).format("D MMM YYYY | HH:mm") : "-",
    },
    {
      title: t("common.text.name"),
      dataIndex: "fullname",
      key: "fullname",
      sorter: true,
      defaultSortOrder:
        tableQuery.order_by === "fullname" && tableQuery.sorted_by,
    },
    {
      title: t("common.text.email"),
      dataIndex: "email",
      key: "email",
      sorter: true,
      defaultSortOrder: tableQuery.order_by === "email" && tableQuery.sorted_by,
    },
    {
      title: t("common.text.phone"),
      dataIndex: "number",
      key: "number",
      sorter: true,
      defaultSortOrder:
        tableQuery.order_by === "number" && tableQuery.sorted_by,
    },
    {
      title: t("common.text.status"),
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (text) => (
        <Tag color={_.get(INBOX_STATUS_COLOR, text, "blue")}>
          {snakeCaseToTitleCase(text)}
        </Tag>
      ),
    },
    {
      title: t("common.text.action"),
      key: "id",
      className: "table-action",
      fixed: "right",
      width: 110,
      render: (text, record) => {
        return (
          <VuiTableActionButton
            detailLink={`/inbox/${record.id}`}
            detailPermissionName="inbox.show"
            deletePermissionName="inbox.destroy"
            showDelete={record.can_delete}
            onClickDelete={() => handleClickDelete(record.id)}
          />
        );
      },
    },
  ];

  const handleClickDelete = useCallback((id: number) => {
    setTableDeleteId(id);
    setShowDeleteModal(true);
  }, []);

  const handleCloseModalDelete = useCallback(() => {
    setTableDeleteId(0);
    setShowDeleteModal(false);
  }, []);

  const handleOnChangeStatus = useCallback((value: ValueType) => {
    uiFilterData.save(LOCALSTORAGE_FILTER_DATA_KEY.status, [value]);
    setSelectedStatus(value);
    setTableQuery((prevState) => ({
      ...prevState,
      page: 1,
      status: value,
    }));
  }, []);

  const handleTableChange = useCallback(
    (pagination: any, filters: any, sorter: any, extra: any) => {
      const params: IPaginationParams = {
        page: pagination.current,
        per_page: pagination.pageSize,
      };

      if (sorter.order) {
        Object.assign(params, {
          sorted_by: sorter.order,
          order_by: sorter.field,
        });
      } else {
        Object.assign(params, {
          sorted_by: null,
          order_by: null,
        });
      }

      setTableQuery(params);
    },
    []
  );

  const handleConfirmDelete = useCallback(async () => {
    await onDelete(tableDeleteId).then(() => {
      handleCloseModalDelete();
      openNotification(
        "success",
        t("notification.success.deleteItem", {
          item: t("common.text.inbox"),
        })
      );

      handleChangeSearchParams();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableDeleteId, handleChangeSearchParams, onDelete, t]);

  useEffect(() => {
    (async () => {
      await handleChangeSearchParams();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableQuery]);

  useEffect(() => {
    if (hasInitialize) {
      setTableQuery((prevState) => ({
        ...prevState,
        search: debouncedSearch,
        page: 1,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  return (
    <>
      <VuiModalConfirmation
        show={showDeleteModal}
        isLoading={deleteLoading}
        onSubmit={handleConfirmDelete}
        onCancel={handleCloseModalDelete}
      />

      <VuiPageTitle title={t("pages.inbox.list.title")}></VuiPageTitle>

      <Row gutter={[10, 10]} className="mb16">
        <Col className="gutter-row" xs={24} md={8}>
          <Input
            allowClear
            placeholder={t("common.form.search.placeholder")}
            prefix={<SearchOutlined />}
            value={search}
            size="middle"
            onChange={(value) => {
              setSearch(value.target.value);
            }}
          />
        </Col>
        <Col className="gutter-row" xs={24} md={4}>
          <VuiSelect
            defaultOptions={inboxStatusOptions(t)}
            value={selectedStatus}
            placeholder={t("common.text.selectStatus")}
            onChange={handleOnChangeStatus}
            className="select-filter"
            size="middle"
          />
        </Col>
      </Row>

      <Table
        rowKey={"id"}
        columns={columns}
        dataSource={data}
        loading={isOnFetchingData}
        onChange={handleTableChange}
        pagination={{
          current: tableQuery.page,
          showSizeChanger: true,
          pageSize: tableQuery.per_page,
          total: totalData,
        }}
        size="small"
        scroll={{ x: "max-content" }}
      />
    </>
  );
};

export default InboxListModule;
